import { Pagination } from 'shared/types/types';
import { TOrganizationRequest, TOrganizationResponsePaginated } from './moderator';
import { edpApiV1 } from './index';

export type GetEmployeesRequest = {
  organizationId: string;
  divisionId?: string;
  search?: string;
  isActive?: boolean;
  page: number;
  size: number;
  personnelNumber?: string;
  allAvailableOrganizations: boolean;
};

export type TEmploee = {
  id: number;
  name: string;
  lastName: string;
  middleName: string;
  image: string;
  division?: {
    divisionId: string;
    name: string;
  };
  personnelNumber: string;
  temporaryPassword?: string;
};

export type GetEmployeesResponse = {
  data: TEmploee[];
  pagination: Pagination;
};

export type GetBlockingResponse = {
  id: number;
  name: string;
  description: string;
  isAuto: boolean;
}[];

export type GetEmployeeBlockingResponse = {
  id: number;
  name: string;
  description: string;
  isAuto: boolean;
}[];

export type DeleteBlockingRequest = {
  employeeId: number;
  blockingId: number;
};

export type EmployeeBlockingRequest = {
  employeeId: number;
  blockingId: number;
};

export const employeesApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    getEmployees: build.query<GetEmployeesResponse, GetEmployeesRequest>({
      query: params => ({ url: '/v1/admin/hr/employee-management/employees', method: 'GET', params }),
      providesTags: ['employees'],
    }),
    getEmployeesLike: build.query<GetEmployeesResponse, GetEmployeesRequest>({
      query: params => ({ url: '/v1/admin/hr/employee-management/employees-ilike-search', method: 'GET', params }),
      providesTags: ['employees'],
    }),
    employeePasswordReset: build.mutation<{ message: string }, number>({
      query: employeeId => ({
        url: `/v1/admin/hr/employee-management/employees/${employeeId}/password-reset`,
        method: 'PATCH',
      }),
      invalidatesTags: ['employees'],
    }),
    refresh1c: build.mutation<{ message: string }, { checkingNumber: number }>({
      query: body => ({
        url: `/v1/admin/hr/employee-management/employees/checkEmployeeFrom1C`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['employees'],
    }),
    employeeBlocking: build.mutation<{ message: string }, EmployeeBlockingRequest>({
      query: ({ employeeId, ...body }) => ({
        url: `/v1/admin/hr/employee-management/employees/${employeeId}/blockings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['employees'],
    }),
    getBlocking: build.query<GetBlockingResponse, null>({
      query: () => ({
        url: `/v1/admin/hr/employee-management/blockings`,
        method: 'GET',
      }),
    }),
    getEmployeeBlockings: build.query<GetEmployeeBlockingResponse, number>({
      query: employeeId => ({
        url: `/v1/admin/hr/employee-management/employees/${employeeId}/blockings`,
        method: 'GET',
      }),
    }),
    deleteBlocking: build.mutation<{ message: string }, DeleteBlockingRequest>({
      query: ({ employeeId, ...body }) => ({
        url: `/v1/admin/hr/employee-management/employees/${employeeId}/blockings`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['employees'],
    }),
    getOrganizations: build.query<TOrganizationResponsePaginated, TOrganizationRequest>({
      query: params => ({ url: '/v1/admin/hr/employee-management/organizations', method: 'GET', params }),
      providesTags: ['employees-organization'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEmployeesQuery,
  useLazyGetEmployeesQuery,
  useLazyGetEmployeesLikeQuery,
  useEmployeePasswordResetMutation,
  useEmployeeBlockingMutation,
  useGetBlockingQuery,
  useRefresh1cMutation,
  useLazyGetEmployeeBlockingsQuery,
  useDeleteBlockingMutation,
  useGetOrganizationsQuery,
  useLazyGetOrganizationsQuery,
} = employeesApi;
